import { lazy, Suspense, type ReactNode } from 'react';
import { HomeSEO } from '@/components/SEO';
import { HomeJornadaNav } from '@/components/HomeJornadaNav';

// ── Above the fold (eager) — pintam já no HTML pré-renderizado ──
import { HeroSection } from '@/sections/home/HeroSection';
import { IntroducaoSection } from '@/sections/guia/IntroducaoSection';
import { MapaMentalSection } from '@/sections/guia/MapaMentalSection';

// ── Below the fold (lazy, com altura reservada p/ CLS 0) ──
// Seções ricas do GUIA (a profundidade de planejamento) + conversão da HOME.
const OQueFazerSection = lazy(() => import('@/sections/home/OQueFazerSection').then(m => ({ default: m.OQueFazerSection })));
const PraiasGuia = lazy(() => import('@/sections/guia/PraiasSection').then(m => ({ default: m.PraiasSection })));
const AtracoesImperdiveisSection = lazy(() => import('@/sections/guia/AtracoesImperdiveisSection').then(m => ({ default: m.AtracoesImperdiveisSection })));

const MelhorEpocaSection = lazy(() => import('@/sections/guia/MelhorEpocaSection').then(m => ({ default: m.MelhorEpocaSection })));
const ComoChegarGuia = lazy(() => import('@/sections/guia/ComoChegarSection').then(m => ({ default: m.ComoChegarSection })));
const QuantoCustaSection = lazy(() => import('@/sections/guia/QuantoCustaSection').then(m => ({ default: m.QuantoCustaSection })));
const ComparativoVilarejosSection = lazy(() => import('@/sections/guia/ComparativoVilarejosSection').then(m => ({ default: m.ComparativoVilarejosSection })));
const OndeFicarGuia = lazy(() => import('@/sections/guia/OndeFicarSection').then(m => ({ default: m.OndeFicarSection })));
const OndeComerSection = lazy(() => import('@/sections/guia/OndeComerSection').then(m => ({ default: m.OndeComerSection })));
const TrilhasSection = lazy(() => import('@/sections/guia/TrilhasSection').then(m => ({ default: m.TrilhasSection })));
const RoteirosGuia = lazy(() => import('@/sections/guia/RoteirosSection').then(m => ({ default: m.RoteirosSection })));
const OQueLevarSection = lazy(() => import('@/sections/guia/OQueLevarSection').then(m => ({ default: m.OQueLevarSection })));
const ConectividadeSection = lazy(() => import('@/sections/guia/ConectividadeSection').then(m => ({ default: m.ConectividadeSection })));
const SegurancaSection = lazy(() => import('@/sections/guia/SegurancaSection').then(m => ({ default: m.SegurancaSection })));
const MapaSection = lazy(() => import('@/sections/guia/MapaSection').then(m => ({ default: m.MapaSection })));

const PousadaDestaqueSection = lazy(() => import('@/sections/guia/PousadaDestaqueSection').then(m => ({ default: m.PousadaDestaqueSection })));
const DepoimentosSection = lazy(() => import('@/sections/home/DepoimentosSection').then(m => ({ default: m.DepoimentosSection })));
const CredibilitySection = lazy(() => import('@/sections/home/CredibilitySection').then(m => ({ default: m.CredibilitySection })));
const FAQSectionGuia = lazy(() => import('@/sections/guia/FAQSectionGuia').then(m => ({ default: m.FAQSectionGuia })));
const WidgetsSection = lazy(() => import('@/sections/home/WidgetsSection').then(m => ({ default: m.WidgetsSection })));
const PilaresSection = lazy(() => import('@/sections/home/PilaresSection').then(m => ({ default: m.PilaresSection })));
const NewsletterSection = lazy(() => import('@/sections/home/NewsletterSection').then(m => ({ default: m.NewsletterSection })));

/** Envolve uma seção lazy com um placeholder de altura reservada — evita CLS quando o chunk chega. */
function Lazy({ children, minH = 'min-h-[520px]' }: { children: ReactNode; minH?: string }) {
  return <Suspense fallback={<div className={minH} aria-hidden="true" />}>{children}</Suspense>;
}

/* ═══════════════════════════════════════════════════════════════
   HOME = GUIA COMPLETO DA ILHA DO MEL
   Jornada de consciência do leitor:
   INÍCIO (desperta desejo) → MEIO (planeja) → FIM (confia + age)
   ═══════════════════════════════════════════════════════════════ */
export function HomePage() {
  return (
    <>
      <HomeSEO />
      <HomeJornadaNav />

      <div className="space-y-0">
        {/* ══ INÍCIO — “Que lugar é esse? Vale a pena? O que tem pra ver?” ══ */}
        <HeroSection />
        <IntroducaoSection />
        <MapaMentalSection />
        <Lazy><OQueFazerSection /></Lazy>
        <Lazy><PraiasGuia /></Lazy>
        <Lazy minH="min-h-[700px]"><AtracoesImperdiveisSection /></Lazy>

        {/* ══ MEIO — “Como eu organizo a viagem?” (o coração do guia) ══ */}
        <Lazy><MelhorEpocaSection /></Lazy>
        <Lazy minH="min-h-[800px]"><ComoChegarGuia /></Lazy>
        <Lazy><QuantoCustaSection /></Lazy>
        <Lazy minH="min-h-[360px]"><ComparativoVilarejosSection /></Lazy>
        <Lazy><OndeFicarGuia /></Lazy>
        <Lazy><OndeComerSection /></Lazy>
        <Lazy><TrilhasSection /></Lazy>
        <Lazy><RoteirosGuia /></Lazy>
        <Lazy><OQueLevarSection /></Lazy>
        <Lazy minH="min-h-[360px]"><ConectividadeSection /></Lazy>
        <Lazy><SegurancaSection /></Lazy>
        <Lazy minH="min-h-[500px]"><MapaSection /></Lazy>

        {/* ══ FIM — “Confio nessa fonte. Vou reservar / salvar / voltar.” ══ */}
        <Lazy><PousadaDestaqueSection /></Lazy>
        <Lazy minH="min-h-[420px]"><DepoimentosSection /></Lazy>
        <Lazy minH="min-h-[220px]"><CredibilitySection /></Lazy>
        <Lazy><FAQSectionGuia /></Lazy>
        <Lazy minH="min-h-[360px]"><WidgetsSection /></Lazy>
        <Lazy minH="min-h-[420px]"><PilaresSection /></Lazy>
        <Lazy minH="min-h-[320px]"><NewsletterSection /></Lazy>
      </div>
    </>
  );
}
